import React, { useContext, useMemo } from 'react';

import ExportButtonContainer from 'components/Grid/ExportButton';
import { GroupVsRemainderProvider } from 'components/GroupVsRemainder/GroupVsRemainderProvider';
import PeerInfoOption from 'components/GroupVsRemainder/PeerInfoOption';
import PopupGridButtonContainer from 'components/GroupVsRemainder/PopupGridButtonContainer';
import RemindersExportButton from 'components/RemindersGadget/RemindersExportButton';
import CardContext from 'contexts/CardContext';
import { Option } from 'kingpin/atoms/ContextMenu';
import isGroupVsRemainder from 'types/visTypeCheckers/isGroupVsRemainder';
import isRemindersGadget from 'types/visTypeCheckers/isRemindersGadget';

import BlockGridApiContext, {
  BlockGridApiProvider,
} from './BlockGridApiContext';
import buildChartDefinition from './buildChartDefinition';
import buildGroupVsRemainderDefinition, {
  isGroupVsRemainderLine,
} from './buildGroupVsRemainderDefinition';
import buildLegacyMatrixDefinition, {
  isLegacyMatrixTable,
} from './buildLegacyMatrixDefinition';
import buildRemindersDefinition, {
  isRemindersTable,
} from './buildRemindersDefinition';
import {
  buildRecordsDefinition,
  isRecordsTable,
  isTotalsTable,
} from './buildTableDefinition';

// A Table of metric totals, drawn as a V5 matrix (a classic Matrix gadget,
// once Studio has saved it)
const isMatrixTable = (content: Studio.ChartContent): boolean =>
  isTotalsTable({ content, canListRecords: true });

// Whether a block's (or Composite element's) menu has options of its own
// (see BlockMenuItems). On a dashboard, a Reminders gadget's menu has its
// export already.
export const hasBlockMenuItems = ({
  content,
  isOnView,
}: {
  content: Studio.ChartContent;
  isOnView: boolean;
}): boolean =>
  isGroupVsRemainderLine(content) ||
  isRecordsTable(content) ||
  isLegacyMatrixTable(content) ||
  isMatrixTable(content) ||
  (isRemindersTable(content) && !isOnView);

// What a block's menu options read from its body, lifted around both: a
// Group vs remainder Line's peer group values, a Table's grid
export const BlockMenuScope = ({
  content,
  children,
}: {
  content: Studio.ChartContent;
  children: JSX.Element;
}) => {
  if (isGroupVsRemainderLine(content)) {
    return <GroupVsRemainderProvider>{children}</GroupVsRemainderProvider>;
  }
  if (
    isRecordsTable(content) ||
    isLegacyMatrixTable(content) ||
    isMatrixTable(content)
  ) {
    return <BlockGridApiProvider>{children}</BlockGridApiProvider>;
  }
  return children;
};

// The options the legacy gadgets' own top bars had, for a Studio block's
// menu (in Studio and on a dashboard or report): a Group vs remainder
// Line's peer group and raw data, a Table's export (totals, records, date
// comparison and pace, once its grid is drawn) and Reminders' export. Under
// the block's scopes, so they see its dates and filters. w and h are the
// grid size of the block (or the Composite the element sits in).
const BlockMenuItems = ({
  content: card,
  name,
  w,
  h,
}: {
  content: Studio.ChartContent;
  name: string;
  w: number;
  h: number;
}) => {
  const { gridApi, exportHandle } = useContext(BlockGridApiContext);
  const built = useMemo(() => {
    if (isGroupVsRemainderLine(card)) {
      return buildGroupVsRemainderDefinition({ card, id: card.id, name });
    }
    if (isRemindersTable(card)) {
      return buildRemindersDefinition({ card, id: card.id, name });
    }
    if (isRecordsTable(card)) {
      return buildRecordsDefinition({ card, name });
    }
    if (isLegacyMatrixTable(card)) {
      return buildLegacyMatrixDefinition({ card, id: card.id, name });
    }
    if (isMatrixTable(card)) {
      return buildChartDefinition({ card, name });
    }
    return undefined;
  }, [card, name]);
  const chartDefinition =
    built && 'chartDefinition' in built ? built.chartDefinition : undefined;
  const cardContextValue = useMemo(
    () => ({ i: card.id, w, h, chartDefinition }),
    [card.id, chartDefinition, h, w],
  );

  if (!chartDefinition) {
    return <></>;
  }
  if (isGroupVsRemainder(chartDefinition)) {
    return (
      <>
        <PeerInfoOption gadget={chartDefinition} />
        <PopupGridButtonContainer chartDefinition={chartDefinition} />
      </>
    );
  }
  return (
    <CardContext.Provider value={cardContextValue}>
      {isRemindersGadget(chartDefinition) ? (
        <RemindersExportButton />
      ) : gridApi || exportHandle ? (
        <ExportButtonContainer
          overrideGridApi={gridApi}
          exportHandle={exportHandle}
          exportName={name}
          isContextMenuItem
          // As the matrix gadgets' own top bars export them
          isTotalsRowIncluded={isLegacyMatrixTable(card) || isMatrixTable(card)}
        />
      ) : (
        <Option
          label="Export"
          isDisabled
          disabledMessage="Available once the table has loaded"
          testId={`studio-block-export-pending-${card.id}`}
        />
      )}
    </CardContext.Provider>
  );
};

export default BlockMenuItems;
